import {
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  forwardRef,
  useCallback,
} from 'react';
import {Upload} from 'lucide-react';
import {Button, type ButtonProps} from '../button';
import {cn} from '../lib/cn';

export interface FileInputButtonProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size'
> {
  label?: string;
  icon?: ReactNode;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  buttonClassName?: string;
}

export const FileInputButton = forwardRef<HTMLInputElement, FileInputButtonProps>(
  (
    {
      label,
      onChange,
      disabled,
      icon,
      className,
      buttonClassName,
      variant = 'outline',
      size = 'default',
      ...props
    },
    ref,
  ) => {
    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onChange?.(event);
        event.target.value = '';
      },
      [onChange],
    );

    const iconElement = icon !== undefined ? icon : <Upload className="h-4 w-4" />;

    return (
      <Button
        asChild
        variant={variant}
        size={size}
        disabled={disabled}
        className={cn('justify-start', buttonClassName)}
      >
        <label className={cn('cursor-pointer', className)}>
          <input
            ref={ref}
            disabled={disabled}
            type="file"
            onChange={handleChange}
            className="absolute hidden h-0.5 w-0.5 opacity-0"
            {...props}
          />
          {iconElement}
          {label && <span className="ml-1.5">{label}</span>}
        </label>
      </Button>
    );
  },
);

FileInputButton.displayName = 'FileInputButton';
